/* eslint-disable-next-line */
import { datadogOptions } from '../config/initBrowserLogging';
// TODO: move this css boilerplate into a next.js plugin
import 'normalize.css'; // css reset
import '@cvent/carina/fonts/fonts.web.css';
import '../styles.css';

import React, { useEffect } from 'react';
import { ApolloProvider, InMemoryCache, makeVar } from '@apollo/client';
import { ApolloCacheProps, ApolloClientFactory, useApollo } from '@cvent/apollo-client';
import ErrorBoundary from '@components/ErrorBoundary';
import { getItem } from '@cvent/nextjs/utils/storage';
import { initRum } from '@cvent/nextjs/datadog/initRum';
import { isWhitelistedRegion } from 'src/utils';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { AppProps } from 'next/app';
import { defaultLocale } from '../../locales';

const { publicRuntimeConfig } = getConfig();

// example of app wide global state, move to another file and import here as this grows!
// to learn more options for local state management go here; https://www.apollographql.com/blog/local-state-management-with-reactive-variables/
export const darkModeVar = makeVar<boolean>(false);
export const clientVersion = makeVar<string>(String(publicRuntimeConfig.CLIENT_VERSION) || 'unknown');
export const localeVar = makeVar<string>(getItem('locale') || defaultLocale);

// Hide internal documentation and examples from the public
const publicPaths = ['/404', '/_error', '/api', '/project'];
const isPublicPath = (path: string): boolean => publicPaths.some(x => path.startsWith(x));

export type AppMainProps = AppProps & {
  pageProps: ApolloCacheProps & {
    locale: string;
  };
};

export default function App({ Component, pageProps }: AppMainProps): JSX.Element {
  initRum(datadogOptions);
  const router = useRouter();
  useEffect(() => {
    if (!isPublicPath(router.pathname) && !isWhitelistedRegion) {
      router.push('/404');
    }
  }, [router]);

  useEffect(() => {
    const { locale } = pageProps;
    // update locale
    if (locale && locale !== localeVar()) {
      localeVar(locale);
    }
  }, [pageProps]);

  const client = useApollo(
    pageProps,
    new ApolloClientFactory({
      loginUrl: (publicRuntimeConfig.LOGIN_URL as string) || '/_error',
      initialState: {},
      errorUrl: '/_error',
      graphBasePath: '/test',
      graphUri: '/test/api/graphql'
    }),
    {
      cache: new InMemoryCache({
        typePolicies: {
          Query: {
            fields: {
              clientVersion: {
                read(): string {
                  return clientVersion();
                }
              },
              darkMode: {
                read(): boolean {
                  return darkModeVar();
                }
              },
              locale: {
                read(): string {
                  return localeVar();
                }
              }
            }
          }
        }
      })
    }
  );

  // Hide internal documentation from public
  if (isPublicPath(router.pathname) || isWhitelistedRegion) {
    return (
      <ApolloProvider client={client}>
        <ErrorBoundary>
          <Component {...pageProps} />
        </ErrorBoundary>
      </ApolloProvider>
    );
  }
  return <p />;
}
